import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
  TextField, Chip, CircularProgress, Alert, Avatar, IconButton,
  Tooltip, Card, CardContent, Button,
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon,
  Person as PersonIcon, AttachMoney as MoneyIcon, 
  Receipt as ReceiptIcon, Download as DownloadIcon,
  CheckCircle as CheckCircleIcon, FilterList as FilterIcon
} from '@mui/icons-material';
import api from '../services/api';

interface User {
  _id: string;
  fullName: string;
  email: string;
  rollNumber: string;
  mobile: string;
  courses: string[];
}

interface Challan {
  _id: string;
  userId: User;
  challanId: string;
  amount: number;
  paid: boolean;
  path?: string;
  branchCode?: number;
  txnDate?: string;
  txnId?: number;
  createdAt: string;
  updatedAt: string;
}

interface ChallanResponse {
  challans: Challan[];
  pagination: { current: number; pages: number; total: number; limit: number };
}

interface ChallanStats {
  total: number;
  paid: number;
  unpaid: number;
  totalRevenue: number;
  pendingRevenue: number;
  recent: number;
}

// Stat Card
const StatCard = ({ label, value, sub, icon, color, bg }: any) => (
  <Card sx={{
    flex: "1 1 240px", borderRadius: "12px", border: "none",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden",
    transition: "transform .25s,box-shadow .25s",
    "&:hover": { transform: "translateY(-4px)", boxShadow: `0 12px 28px rgba(0,0,0,0.1)` },
  }}>
    <Box sx={{ height: 3, background: `linear-gradient(90deg,${color},#fbbf24)` }} />
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>{label}</Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: "#1e293b", lineHeight: 1 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 400, mt: 0.5 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Payments = () => {
  const [payments, setPayments] = useState<Challan[]>([]);
  const [stats, setStats] = useState<ChallanStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  useEffect(() => {
    fetchPayments();
  }, [page, rowsPerPage, search, startDate, endDate, minAmount, maxAmount]);

  useEffect(() => {
    fetchPaymentStats();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params: any = { 
        page: page + 1, 
        limit: rowsPerPage, 
        project: "DigiSindh",
        paid: "true" // Only fetch paid challans (payments)
      };
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (minAmount) params.minAmount = minAmount;
      if (maxAmount) params.maxAmount = maxAmount;

      const response = await api.get("/challans", { params });
      const data: ChallanResponse = response.data.data;
      setPayments(data.challans);
      setTotal(data.pagination.total);
    } catch (err) {
      setError("Failed to load payments database.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentStats = async () => {
    try {
      const response = await api.get("/challans/stats", { params: { project: "DigiSindh" } });
      setStats(response.data.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);
  };
  const clearFilters = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setMinAmount("");
    setMaxAmount("");
    setPage(0);
  };

  const downloadPayments = async () => {
    try {
      setLoading(true);
      const params: any = { paid: "true" };
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (minAmount) params.minAmount = minAmount;
      if (maxAmount) params.maxAmount = maxAmount;

      const response = await api.get("/challans/download", { params, responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().split("T")[0];
      link.download = `payments_report_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to export payments list");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d?: string) => !d ? "-" : new Date(d).toLocaleDateString("en-PK", {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  
  const formatCurrency = (amount: number) => new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0
  }).format(amount);

  const getInitials = (name?: string) => !name ? "" : name.split(" ").map(n => n[0]).join("").toUpperCase();
  const getActiveFilterCount = () => [search, startDate, endDate, minAmount, maxAmount].filter(Boolean).length;

  const totalPayments = stats?.paid || 0;
  const totalRevenue = stats?.totalRevenue || 0;
  const averagePayment = totalPayments > 0 ? Math.round(totalRevenue / totalPayments) : 0;

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: '#059669' }}>
            Payments & Receipts
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400, mt: 0.25 }}>
            {totalPayments > 0 ? `${totalPayments.toLocaleString()} verified payments collected` : "Loading verified transactions..."}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Export CSV Report">
            <IconButton onClick={downloadPayments} disabled={loading} sx={{ bgcolor: "#f1f5f9", borderRadius: "12px", "&:hover": { bgcolor: "#e2e8f0" } }}>
              <DownloadIcon sx={{ color: "#059669" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh Database">
            <IconButton onClick={fetchPayments} disabled={loading} sx={{ bgcolor: "#f1f5f9", borderRadius: "12px", "&:hover": { bgcolor: "#e2e8f0" } }}>
              <RefreshIcon sx={{ color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

      {/* Stat Cards */}
      <Box display="flex" flexWrap="wrap" gap={2.5} mb={3}>
        <StatCard label="Total Collected Revenue" value={formatCurrency(totalRevenue)} sub="Gross collection" icon={<MoneyIcon />} color="#059669" bg="rgba(5,150,105,0.1)" />
        <StatCard label="Verified Transactions" value={totalPayments.toLocaleString()} sub="Paid challans" icon={<CheckCircleIcon />} color="#059669" bg="rgba(5,150,105,0.1)" />
        <StatCard label="Average Transaction" value={formatCurrency(averagePayment)} sub="Per payment amount" icon={<ReceiptIcon />} color="#fbbf24" bg="rgba(251,191,36,0.1)" />
      </Box>

      {/* Filters Card */}
      <Card sx={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <FilterIcon sx={{ color: "#059669", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b" }}>Filters</Typography>
              {getActiveFilterCount() > 0 && (
                <Chip label={`${getActiveFilterCount()} active`} size="small" sx={{ bgcolor: "#059669", color: "white", fontWeight: 700, fontSize: "0.7rem" }} />
              )}
            </Box>
            {getActiveFilterCount() > 0 && (
              <Button size="small" onClick={clearFilters} sx={{ color: "#ef4444", fontWeight: 600, fontSize: "0.78rem", textTransform: "none" }}>
                Clear All
              </Button>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <TextField size="small" label="Search Student / Challan" placeholder="Name, Roll No, Challan ID, Txn ID" value={search} onChange={handleSearchChange}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "#94a3b8", fontSize: 18 }} /> }}
              sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <TextField size="small" label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <TextField size="small" label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <TextField size="small" label="Min Amount" type="number" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <TextField size="small" label="Max Amount" type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
          </Box>
        </CardContent>
      </Card>

      {/* Payments Table Card */}
      <Card sx={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                {["Student", "Roll & Mobile", "Challan ID", "Transaction details", "Amount Paid", "Status", "Receipt Date"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#059669", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #e2e8f0", py: 1.8 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 8 }}><CircularProgress size={36} sx={{ color: "#059669" }} /></TableCell></TableRow>
              ) : payments.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 8, color: "#94a3b8", fontWeight: 500 }}>No payments record found</TableCell></TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment._id} hover sx={{ "&:hover": { bgcolor: "#f8fafc" }, transition: "background .15s" }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: "#059669", width: 36, height: 36, fontSize: "0.8rem", fontWeight: 700 }}>
                          {payment.userId?.fullName ? getInitials(payment.userId.fullName) : <PersonIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#1e293b" }}>{payment.userId?.fullName || "Unknown"}</Typography>
                          <Typography sx={{ fontSize: "0.73rem", color: "#94a3b8" }}>{payment.userId?.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.8rem", color: "#334155", fontWeight: 500 }}>
                        {payment.userId?.rollNumber ? (payment.userId.rollNumber.startsWith("DS") ? "HS" + payment.userId.rollNumber.slice(2) : payment.userId.rollNumber) : "-"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>M: {payment.userId?.mobile || "N/A"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#475569" }}>
                        {payment.challanId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.txnId ? (
                        <Box>
                          <Typography sx={{ fontSize: "0.8rem", color: "#334155", fontWeight: 500 }}>ID: {payment.txnId}</Typography>
                          {payment.branchCode && <Typography sx={{ fontSize: "0.73rem", color: "#94a3b8" }}>Branch: {payment.branchCode}</Typography>}
                        </Box>
                      ) : <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>1Bill Instant Transfer</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#059669" }}>
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<CheckCircleIcon sx={{ '&&': { color: '#059669' } }} />}
                        label="VERIFIED"
                        size="small"
                        sx={{
                          fontWeight: 700, fontSize: "0.68rem",
                          bgcolor: "rgba(5,150,105,0.1)",
                          color: "#059669",
                          border: "none",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
                        {formatDate(payment.txnDate || payment.updatedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={total} rowsPerPage={rowsPerPage} page={page} onPageChange={handlePageChange} onRowsPerPageChange={handleRowsPerPageChange} />
      </Card>
    </Box>
  );
};

export default Payments;
